/*
  LogoRail - THE one logo marquee for the whole site (client, 2026-10-08: the
  Ultimate Lap partners rail kept glitching / dropping logos in Safari while the
  landing one looked fine - so both now render through this single component,
  identical except for colours and sizes set by the parent in CSS).

  Built to be boring for the GPU:
    - No mask-image. The edge fade is two gradient overlays in the band colour
      (--rail-bg). Safari combines mask + animated transform + very wide layer
      badly (tiles go missing = "logos vanishing").
    - No will-change, no 5000px layer. The logos are rendered as N identical
      GROUPS laid side by side; every group animates translateX(0 -> -100%) of
      ITS OWN width, so each group is a small layer and the hand-off is exact.
    - N is measured: enough groups to cover the viewport plus one, so there is
      never a hole on wide screens, and never more layers than needed.
    - Held at the start until the page loader has revealed the site, paused
      while off-screen or when the tab is hidden.
*/
import { useEffect, useRef, useState } from "react";
import { LOADER_DONE } from "./Loader";

export function LogoRail({ logos, speed = 24, className = "", testid = "logo-rail", label }) {
  const ref = useRef(null);
  const firstGroup = useRef(null);
  const [copies, setCopies] = useState(3);

  useEffect(() => {
    const root = ref.current;
    if (!root) return undefined;
    const live = () => root.classList.add("is-live");
    if (window.__gmLoaderDone) live();
    else window.addEventListener(LOADER_DONE, live, { once: true });
    let inView = true;
    const setPaused = () => root.classList.toggle("is-paused", document.hidden || !inView);
    let io;
    if ("IntersectionObserver" in window) {
      io = new IntersectionObserver((entries) => { inView = !!entries[0]?.isIntersecting; setPaused(); }, { threshold: 0 });
      io.observe(root);
    }
    document.addEventListener("visibilitychange", setPaused);
    return () => {
      window.removeEventListener(LOADER_DONE, live);
      io?.disconnect();
      document.removeEventListener("visibilitychange", setPaused);
    };
  }, []);

  // How many groups cover the rail? Re-measured when the rail or the first
  // group changes size (images landing, viewport resize, font load).
  useEffect(() => {
    const root = ref.current;
    const g = firstGroup.current;
    if (!root || !g || !("ResizeObserver" in window)) return undefined;
    const measure = () => {
      const gw = g.getBoundingClientRect().width;
      const rw = root.getBoundingClientRect().width;
      if (gw < 10 || rw < 10) return;
      const n = Math.max(2, Math.ceil(rw / gw) + 1);
      setCopies((c) => (c === n ? c : n));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    ro.observe(g);
    measure();
    return () => ro.disconnect();
  }, [logos]);

  return (
    <div className={`gm-rail ${className}`} ref={ref} data-testid={testid} aria-label={label} style={{ "--rail-dur": `${speed}s` }}>
      {Array.from({ length: copies }).map((_, c) => (
        <div className="gm-rail__group" key={c} ref={c === 0 ? firstGroup : undefined} aria-hidden={c > 0 ? "true" : undefined} data-testid={c === 0 ? `${testid}-group` : undefined}>
          {logos.map((l) => (
            <div className="gm-rail__item" key={l.src}>
              <img src={l.src} alt={c === 0 ? l.name : ""} loading="eager" decoding="async" draggable="false" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}
